import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { getValidAccessToken } from "../utils/ga4Tokens"

const normalizeChannelGroupName = (value, fallback = null) => {
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (trimmed) return trimmed
  }
  return fallback
}

const normalizeChannelGroupDefinition = (group) => {
  if (!group || typeof group !== "object") return null
  const displayName = normalizeChannelGroupName(group.displayName, "(not set)")
  if (!displayName) return null
  const key = displayName.toLowerCase()
  if (!key) return null
  const primary =
    group.primary === true
      ? true
      : group.primary === false
        ? false
        : null
  const systemDefined =
    group.systemDefined === true
      ? true
      : group.systemDefined === false
        ? false
        : null
  const description =
    typeof group.description === "string" && group.description.trim()
      ? group.description.trim()
      : null
  const resourceName =
    typeof group.resourceName === "string" && group.resourceName.trim()
      ? group.resourceName.trim()
      : typeof group.name === "string" && group.name.trim()
        ? group.name.trim()
        : null
  const rawRules = Array.isArray(group.groupingRules)
    ? group.groupingRules
    : Array.isArray(group.groupingRule)
      ? group.groupingRule
      : []
  const groupingRules = rawRules
    .map((rule, idx) => {
      if (!rule || typeof rule !== "object") return null
      const rawLabel =
        typeof rule.displayName === "string" ? rule.displayName : ""
      const label = rawLabel.trim() || `Rule ${idx + 1}`
      const expression =
        rule.expression && typeof rule.expression === "object"
          ? rule.expression
          : null
      return {
        displayName: rawLabel || label,
        normalizedName: label,
        expression,
        raw: rule,
      }
    })
    .filter(Boolean)
  return {
    key,
    displayName,
    normalizedName: displayName,
    primary,
    systemDefined,
    description,
    resourceName,
    groupingRules,
    raw: group,
  }
}

const normalizeChannelGroupCollection = (collection) => {
  if (!Array.isArray(collection)) return []
  const seen = new Set()
  const result = []
  collection.forEach((item) => {
    const normalized = normalizeChannelGroupDefinition(item)
    if (!normalized) return
    if (seen.has(normalized.key)) return
    seen.add(normalized.key)
    result.push(normalized)
  })
  return result
}

const extractTemplateChannelGroups = (template) => {
  if (!template || typeof template !== "object") return []
  const rawGroups = Array.isArray(template.channelGroupings)
    ? template.channelGroupings
    : []
  return normalizeChannelGroupCollection(rawGroups)
}

const formatIssueType = (issueType) => {
  if (!issueType) return "Unknown issue"
  const key = String(issueType).toLowerCase()
  if (key === "missing_group") return "Channel group missing"
  if (key === "missing_rule") return "Rule missing"
  if (key === "rule_mismatch") return "Rule mismatch"
  if (key === "unexpected_group") return "Unexpected channel group"
  if (key === "unexpected_rule") return "Unexpected rule"
  return issueType
}

const describeFilter = (filter) => {
  if (!filter || typeof filter !== "object") return null
  const target =
    filter.fieldName ||
    filter.field ||
    filter.parameterName ||
    filter.dimensionName ||
    filter.eventName ||
    filter.name ||
    "condition"
  const stringFilter = filter.stringFilter || filter.string_filter
  if (stringFilter) {
    const matchType =
      stringFilter.matchType ||
      stringFilter.match_type ||
      stringFilter.match_type ||
      "matches"
    const value = stringFilter.value ?? stringFilter.caseSensitiveValue ?? ""
    return `${target} ${String(matchType).replace(/_/g, " ").toLowerCase()} "${value}"`
  }
  const inList = filter.inListFilter || filter.in_list_filter
  if (inList?.values?.length) {
    return `${target} in [${inList.values.join(", ")}]`
  }
  const numeric = filter.numericFilter || filter.numeric_filter
  if (numeric) {
    const op = numeric.operation || numeric.op || "="
    const value =
      numeric.value?.doubleValue ??
      numeric.value?.int64Value ??
      numeric.value ??
      "value"
    return `${target} ${String(op).replace(/_/g, " ")} ${value}`
  }
  const between = filter.betweenFilter || filter.between_filter
  if (between) {
    const from =
      between.fromValue?.doubleValue ??
      between.fromValue?.int64Value ??
      between.fromValue ??
      "?"
    const to =
      between.toValue?.doubleValue ??
      between.toValue?.int64Value ??
      between.toValue ??
      "?"
    return `${target} between ${from} and ${to}`
  }
  const regex = filter.regexMatch || filter.regex_filter || filter.regexMatch
  if (regex) {
    return `${target} matches /${regex.pattern || regex}/`
  }
  return null
}

const extractFilterDescriptions = (expression) => {
  if (!expression) return []
  if (Array.isArray(expression)) {
    return expression.flatMap((item) => extractFilterDescriptions(item)).filter(Boolean)
  }
  if (typeof expression !== "object") return []

  const andGroup = expression.andGroup?.filterExpressions
  if (Array.isArray(andGroup) && andGroup.length) {
    const parts = andGroup.flatMap((item) => extractFilterDescriptions(item)).filter(Boolean)
    if (parts.length) return [`(${parts.join(" AND ")})`]
  }

  const orGroup = expression.orGroup?.filterExpressions
  if (Array.isArray(orGroup) && orGroup.length) {
    const parts = orGroup.flatMap((item) => extractFilterDescriptions(item)).filter(Boolean)
    if (parts.length) return [`(${parts.join(" OR ")})`]
  }

  if (expression.notExpression) {
    const parts = extractFilterDescriptions(expression.notExpression).filter(Boolean)
    if (parts.length) return [`NOT ${parts.join(" ")}`]
  }

  const filterDescription = describeFilter(expression.filter || expression)
  if (filterDescription) return [filterDescription]

  if (expression.filterExpression) {
    return extractFilterDescriptions(expression.filterExpression)
  }

  return []
}

const summarizeRuleConditions = (rule) => {
  const expression =
    rule?.expression || rule?.raw?.expression || rule?.raw || rule?.filterExpression
  const parts = extractFilterDescriptions(expression)
  if (!parts.length) return "No conditions defined."
  return parts.join("; ")
}

const normalizeRuleLabel = (rule, fallbackIndex = null) => {
  const rawLabel =
    (typeof rule?.displayName === "string" && rule.displayName) ||
    (typeof rule?.normalizedName === "string" && rule.normalizedName) ||
    ""
  const label = rawLabel.trim()
  if (label) return label
  if (typeof fallbackIndex === "number") return `Rule ${fallbackIndex + 1}`
  return "Unnamed rule"
}

const renderRuleSummary = (rule, fallbackIndex = null) => {
  if (!rule) return "—"
  const label = normalizeRuleLabel(rule, fallbackIndex)
  const conditions = summarizeRuleConditions(rule)
  return (
    <div>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div style={{ marginTop: "0.25rem", fontSize: "0.9rem" }}>{conditions}</div>
    </div>
  )
}

const renderRuleLabel = (issue) => {
  const name = issue?.rule_display_name
  const index =
    typeof issue?.rule_index === "number" && Number.isFinite(issue.rule_index)
      ? issue.rule_index
      : null
  if (name && index !== null) return `${name} (rule ${index + 1})`
  if (name) return name
  if (index !== null) return `Rule ${index + 1}`
  return "—"
}

const buildIssueExplanation = (issue) => {
  const typeKey = String(issue?.issue_type || "").toLowerCase()
  const channelGroup = issue?.channel_group_name || "the channel group"
  const ruleLabel = issue ? renderRuleLabel(issue) : null

  if (typeKey === "missing_group") {
    const expectedRule = issue?.expected_rule
    const ruleSummary =
      expectedRule && typeof expectedRule === "object"
        ? ` Ensure the group includes rule ${normalizeRuleLabel(
            expectedRule,
            issue?.rule_index ?? null,
          )}.`
        : ""
    return `Template expects channel group "${channelGroup}" but it is missing from the property.${ruleSummary} Add the group and replicate the template rules.`
  }

  if (typeKey === "missing_rule") {
    return `Template expects ${ruleLabel || "a rule"} in "${channelGroup}", but it is missing in the property. Add the rule using the template conditions.`
  }

  if (typeKey === "rule_mismatch") {
    return `Rule ${ruleLabel || ""} in "${channelGroup}" does not match the template. Update the property rule to align with the expected conditions.`
  }

  if (typeKey === "unexpected_group") {
    return `Channel group "${channelGroup}" exists in the property but not in the template. Remove it or add it to the template if it should be standard.`
  }

  if (typeKey === "unexpected_rule") {
    return `Property contains extra rule ${ruleLabel || ""} in "${channelGroup}" that is not in the template. Remove it or adjust the template if this rule is required.`
  }

  return "Review this issue and align the property with the template."
}

const buildChannelGroupSummary = (
  propertyGroups,
  templateGroups,
  templateIssues,
  hasTemplate,
) => {
  const summary = new Map()

  const ensureEntry = (rawName) => {
    const label = normalizeChannelGroupName(rawName, "(not set)")
    const key = label.toLowerCase()
    let entry = summary.get(key)
    if (!entry) {
      entry = {
        key,
        name: label,
        displayName: label,
        propertyDefinition: null,
        templateDefinition: null,
        notes: new Set(),
      }
      summary.set(key, entry)
    }
    return entry
  }

  const propertyList = Array.isArray(propertyGroups) ? propertyGroups : []
  propertyList.forEach((group) => {
    const entry = ensureEntry(group?.normalizedName || group?.displayName)
    if (!entry) return
    entry.displayName = group.displayName || entry.displayName
    entry.propertyDefinition = group
  })

  const templateGroupList = Array.isArray(templateGroups) ? templateGroups : []
  templateGroupList.forEach((group) => {
    const entry = ensureEntry(group?.normalizedName || group?.displayName)
    if (!entry) return
    if (!entry.propertyDefinition) {
      entry.displayName = group.displayName || entry.displayName
    }
    entry.templateDefinition = group
  })

  const issueList = Array.isArray(templateIssues) ? templateIssues : []
  issueList.forEach((issue) => {
    const issueName =
      normalizeChannelGroupName(issue?.channel_group_name) ||
      normalizeChannelGroupName(issue?.expected_rule?.displayName) ||
      normalizeChannelGroupName(issue?.actual_rule?.displayName) ||
      "(not set)"
    const entry = ensureEntry(issueName)
    const note = formatIssueType(issue?.issue_type)
    if (note) entry.notes.add(note)
    const typeKey = String(issue?.issue_type || "").toLowerCase()
    if (typeKey === "missing_group" && issue?.expected_rule) {
      const normalizedExpected = normalizeChannelGroupDefinition(issue.expected_rule)
      if (normalizedExpected && !entry.templateDefinition) {
        entry.templateDefinition = normalizedExpected
        if (!entry.propertyDefinition) {
          entry.displayName = normalizedExpected.displayName || entry.displayName
        }
      }
    } else if (typeKey === "unexpected_group" && issue?.actual_rule) {
      const normalizedActual = normalizeChannelGroupDefinition(issue.actual_rule)
      if (normalizedActual && !entry.propertyDefinition) {
        entry.propertyDefinition = normalizedActual
        entry.displayName = normalizedActual.displayName || entry.displayName
      }
    }
  })

  const statusPriority = {
    "Needs review": 0,
    "Not in template": 1,
    "Missing from property": 2,
    "Matches template": 3,
    "In property": 4,
    "Not detected": 5,
  }

  return Array.from(summary.values())
    .map((entry) => {
      const propertyDefinition = entry.propertyDefinition
      const templateDefinition = entry.templateDefinition
      const propertyExists = Boolean(propertyDefinition)
      const templateExists = Boolean(templateDefinition)
      const hasNotes = entry.notes.size > 0
      let status = "Not detected"
      if (propertyExists) {
        if (hasTemplate) {
          status = templateExists
            ? hasNotes
              ? "Needs review"
              : "Matches template"
            : "Not in template"
        } else {
          status = "In property"
        }
      } else if (templateExists) {
        status = "Missing from property"
      }
      const propertyPrimary =
        propertyDefinition?.primary === true
          ? true
          : propertyDefinition?.primary === false
            ? false
            : null
      const templatePrimary =
        templateDefinition?.primary === true
          ? true
          : templateDefinition?.primary === false
            ? false
            : null
      const inTemplate = hasTemplate
        ? templateExists
          ? true
          : false
        : null
      const systemDefined =
        propertyDefinition?.systemDefined === true
          ? true
          : propertyDefinition?.systemDefined === false
            ? false
            : null
      const resourceName = propertyDefinition?.resourceName || null
      const description =
        propertyDefinition?.description || templateDefinition?.description || null
      return {
        key: entry.key,
        name: entry.displayName,
        status,
        systemDefined,
        propertyPrimary,
        templatePrimary,
        inTemplate,
        notes: Array.from(entry.notes),
        propertyDefinition,
        templateDefinition,
        resourceName,
        description,
      }
    })
    .sort((a, b) => {
      const statusDiff =
        (statusPriority[a.status] ?? 99) - (statusPriority[b.status] ?? 99)
      if (statusDiff !== 0) return statusDiff
      return a.name.localeCompare(b.name)
    })
}


export default function GA4ChannelGroupingAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const user = useUser()
  const [propertyGroups, setPropertyGroups] = useState([])
  const [templateIssues, setTemplateIssues] = useState([])
  const [templateGroups, setTemplateGroups] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const [expandedRows, setExpandedRows] = useState(() => new Set())
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  useEffect(() => {
    if (propertyLoading) {
      console.debug("[GA4][ChannelGroupingAudit] Waiting for property to load", {
        propertyId,
      })
      return
    }

    if (!user) {
      setAuditLoading(true)
      return
    }

    if (propertyError || !propInfo?.id) {
      console.error(
        "[GA4][ChannelGroupingAudit] Unable to load property context",
        {
          propertyId,
          propertyError,
          hasPropInfo: Boolean(propInfo),
        }
      )
      setPropertyGroups([])
      setTemplateIssues([])
      setTemplateGroups([])
      setExpandedRows(new Set())
      setAuditError(propertyError || null)
      setAuditLoading(false)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      setExpandedRows(new Set())
      setTemplateGroups([])
      setTemplateIssues([])
      setPropertyGroups([])
      try {
        console.log("[GA4][ChannelGroupingAudit] Loading channel groups", {
          propertyId,
          ga4PropertyId: propInfo.id,
          ga4ResourceId: propInfo.property_id,
        })
        const templatePromise = propInfo?.template_id
          ? supabase
              .from("ga4_templates")
              .select("template")
              .eq("id", propInfo.template_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null })
        const [templateResult, templateDefinition] = await Promise.all([
          supabase
            .from("ga4_channel_group_template_audit_results")
            .select(
              "issue_type, channel_group_name, rule_display_name, rule_index, expected_rule, actual_rule",
            )
            .eq("ga4_property_id", propInfo.id)
            .order("issue_type", { ascending: true }),
          templatePromise,
        ])

        if (templateResult.error) throw templateResult.error

        const templateRows = Array.isArray(templateResult.data)
          ? templateResult.data.map((row) => ({
              issue_type: row?.issue_type || "",
              channel_group_name: row?.channel_group_name || null,
              rule_display_name: row?.rule_display_name || null,
              rule_index:
                typeof row?.rule_index === "number" && Number.isFinite(row.rule_index)
                  ? row.rule_index
                  : null,
              expected_rule:
                row?.expected_rule && typeof row.expected_rule === "object"
                  ? row.expected_rule
                  : null,
              actual_rule:
                row?.actual_rule && typeof row.actual_rule === "object"
                  ? row.actual_rule
                  : null,
            }))
          : []
        if (templateDefinition?.error) {
          console.error(
            "[GA4][ChannelGroupingAudit] Failed to load template definition",
            {
              propertyId,
              ga4PropertyId: propInfo.id,
              templateId: propInfo.template_id,
              error: templateDefinition.error,
            },
          )
        }
        const templateGroupList = extractTemplateChannelGroups(
          templateDefinition?.data?.template,
        )
        const rawPropertyId =
          typeof propInfo?.property_id === "string" && propInfo.property_id.trim()
            ? propInfo.property_id.trim()
            : ""
        const normalizedPropertyId = rawPropertyId.replace(/^properties\//, "")
        if (!normalizedPropertyId) {
          throw new Error("Missing GA4 property resource identifier")
        }

        let accessToken
        try {
          accessToken = await getValidAccessToken(supabase, user)
        } catch (tokenErr) {
          console.error(
            "[GA4][ChannelGroupingAudit] Failed to resolve GA4 access token",
            {
              propertyId,
              ga4PropertyId: propInfo.id,
              ga4ResourceId: propInfo.property_id,
              error: tokenErr,
            },
          )
          throw tokenErr
        }

        if (!accessToken) {
          throw new Error("Missing GA4 access token")
        }

        const { data: propertyGroupResponse, error: propertyGroupError } =
          await supabase.functions.invoke("ga4-proxy", {
            body: { type: "channel-group-list", propertyId: normalizedPropertyId },
            headers: { "x-ga4-authorization": `Bearer ${accessToken}` },
          })

        if (propertyGroupError) throw propertyGroupError

        const propertyGroupList = normalizeChannelGroupCollection(
          Array.isArray(propertyGroupResponse) ? propertyGroupResponse : [],
        )

        console.log("[GA4][ChannelGroupingAudit] Channel group data loaded", {
          propertyId,
          ga4PropertyId: propInfo.id,
          ga4ResourceId: propInfo.property_id,
          propertyGroups: propertyGroupList.length,
          templateIssues: templateRows.length,
          templateGroups: templateGroupList.length,
        })
        if (cancelled) return
        setPropertyGroups(propertyGroupList)
        setTemplateIssues(templateRows)
        setTemplateGroups(templateGroupList)
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 channel grouping audit", {
          propertyId,
          ga4PropertyId: propInfo.id,
          ga4ResourceId: propInfo.property_id,
          error: err,
        })
        setPropertyGroups([])
        setTemplateIssues([])
        setTemplateGroups([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [
    user,
    propInfo,
    propInfo?.id,
    propInfo?.property_id,
    propertyId,
    propertyLoading,
    propertyError,
  ])

  const loading = propertyLoading || auditLoading
  const hasTemplate = Boolean(propInfo?.template_id)

  const channelGroupSummary = useMemo(
    () =>
      buildChannelGroupSummary(
        propertyGroups,
        templateGroups,
        templateIssues,
        hasTemplate,
      ),
    [propertyGroups, templateGroups, templateIssues, hasTemplate],
  )

  const detailColumnCount = hasTemplate ? 7 : 5

  const toggleRow = useCallback((key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const renderBoolean = (value) => {
    if (value === true) return "Yes"
    if (value === false) return "No"
    return "—"
  }

  const renderGroupingRules = (definition) => {
    const rules = Array.isArray(definition?.groupingRules)
      ? definition.groupingRules
      : []
    if (!rules.length) {
      return (
        <p style={{ margin: "0.5rem 0 0", fontStyle: "italic" }}>
          No rules defined.
        </p>
      )
    }
    return (
      <ol style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
        {rules.map((rule, idx) => {
          const label = rule?.displayName || `Rule ${idx + 1}`
          return (
            <li
              key={`${definition?.key || definition?.displayName || "rule"}-${idx}`}
              style={{ marginBottom: idx === rules.length - 1 ? 0 : "0.75rem" }}
            >
              <div style={{ fontWeight: 600 }}>{label}</div>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontSize: "0.85rem",
                  marginTop: "0.25rem",
                }}
              >
                {renderRuleJson(rule?.expression ?? rule?.raw)}
              </pre>
            </li>
          )
        })}
      </ol>
    )
  }

  const renderDefinitionSection = (definition, title, emptyMessage) => (
    <div>
      <h4 style={{ margin: 0, fontSize: "1rem" }}>{title}</h4>
      {definition ? (
        <>
          {definition.resourceName ? (
            <p style={{ margin: "0.5rem 0 0" }}>
              <span style={{ fontWeight: 600 }}>Resource:</span>{" "}
              <code>{definition.resourceName}</code>
            </p>
          ) : null}
          {definition.description ? (
            <p style={{ margin: definition.resourceName ? "0.5rem 0 0" : "0.5rem 0 0" }}>
              {definition.description}
            </p>
          ) : null}
          {renderGroupingRules(definition)}
        </>
      ) : (
        <p style={{ margin: "0.5rem 0 0" }}>{emptyMessage}</p>
      )}
    </div>
  )

  const renderRuleJson = (value) => {
    if (!value) return "—"
    try {
      return JSON.stringify(value, null, 2)
    } catch (err) {
      return String(value)
    }
  }

  return (
    <div className="page-container">
      <h2>Channel Grouping Audit</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load channel grouping audit data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          <div style={{ marginTop: "1rem" }}>
            <h3 style={{ marginBottom: "0.5rem" }}>Channel Groups</h3>
            <p style={{ marginTop: 0, fontSize: "0.9rem" }}>
              Channel groups below reflect the definitions configured in GA4. Expand
              a row to review the underlying rules and compare them with the linked
              template.
            </p>
            {channelGroupSummary.length ? (
              <table
                className="ui-table"
                style={{ width: "100%", marginTop: "0.75rem" }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Channel Group</th>
                    <th style={{ textAlign: "left" }}>Status</th>
                    <th style={{ textAlign: "center" }}>System defined</th>
                    <th style={{ textAlign: "center" }}>Property Primary</th>
                    {hasTemplate ? (
                      <>
                        <th style={{ textAlign: "center" }}>Template Primary</th>
                        <th style={{ textAlign: "center" }}>In Template</th>
                      </>
                    ) : null}
                    <th style={{ textAlign: "left" }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {channelGroupSummary.map((group) => {
                    const isExpanded = expandedRows.has(group.key)
                    return (
                      <Fragment key={group.key}>
                        <tr>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => toggleRow(group.key)}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.name}`}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  fontSize: "1.2rem",
                                  lineHeight: 1,
                                  padding: 0,
                                }}
                              >
                                {isExpanded ? "−" : "+"}
                              </button>
                              <div>
                                <div>{group.name}</div>
                                {group.resourceName ? (
                                  <div
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "#555",
                                      marginTop: "0.15rem",
                                    }}
                                  >
                                    <code>{group.resourceName}</code>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>{group.status}</td>
                          <td style={{ textAlign: "center" }}>
                            {renderBoolean(group.systemDefined)}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {renderBoolean(group.propertyPrimary)}
                          </td>
                          {hasTemplate ? (
                            <>
                              <td style={{ textAlign: "center" }}>
                                {renderBoolean(group.templatePrimary)}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {renderBoolean(group.inTemplate)}
                              </td>
                            </>
                          ) : null}
                          <td>
                            {group.notes.length ? group.notes.join(", ") : "—"}
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={detailColumnCount}>
                              <div
                                style={{
                                  padding: "0.75rem 1rem",
                                  backgroundColor: "#f9fafb",
                                  borderRadius: "0.5rem",
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gap: "1.5rem",
                                    gridTemplateColumns:
                                      hasTemplate && group.templateDefinition
                                        ? "repeat(2, minmax(0, 1fr))"
                                        : "minmax(0, 1fr)",
                                  }}
                                >
                                  {renderDefinitionSection(
                                    group.propertyDefinition,
                                    "Property definition",
                                    "No property channel group definition detected.",
                                  )}
                                  {hasTemplate
                                    ? renderDefinitionSection(
                                        group.templateDefinition,
                                        "Template definition",
                                        "No matching template channel group definition.",
                                      )
                                    : null}
                                </div>
                                {group.notes.length ? (
                                  <div style={{ marginTop: "1rem" }}>
                                    <h4 style={{ margin: 0, fontSize: "1rem" }}>Notes</h4>
                                    <ul
                                      style={{
                                        margin: "0.5rem 0 0",
                                        paddingLeft: "1.25rem",
                                      }}
                                    >
                                      {group.notes.map((note, noteIdx) => (
                                        <li key={`note-${group.key}-${noteIdx}`}>{note}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <p style={{ marginTop: "0.75rem" }}>No channel groups found.</p>
            )}
          </div>
          <div style={{ marginTop: "2rem" }}>
            <h3 style={{ marginBottom: "0.75rem" }}>Template Alignment</h3>
            {!propInfo?.template_id ? (
              <p>This property is not linked to a GA4 template.</p>
            ) : templateIssues.length ? (
              <table className="ui-table"
                border="1"
                cellPadding="8"
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Issue</th>
                    <th style={{ textAlign: "left" }}>Channel Group</th>
                    <th style={{ textAlign: "left" }}>Rule</th>
                    <th style={{ textAlign: "left" }}>Explanation</th>
                    <th style={{ textAlign: "left" }}>Expected</th>
                    <th style={{ textAlign: "left" }}>Actual</th>
                  </tr>
                </thead>
                <tbody>
                  {templateIssues.map((issue, index) => (
                    <tr key={`template-issue-${index}`}>
                      <td>{formatIssueType(issue.issue_type)}</td>
                      <td>{issue.channel_group_name || "—"}</td>
                      <td>{renderRuleLabel(issue)}</td>
                      <td>{buildIssueExplanation(issue)}</td>
                      <td>{renderRuleSummary(issue.expected_rule, issue.rule_index)}</td>
                      <td>{renderRuleSummary(issue.actual_rule, issue.rule_index)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No mismatches detected between the GA4 property and template.</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
